// OnboardingDetailPage-part1.tsx
// PART 1: Imports, Types, Utilities, and Badge Components

import React from 'react';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  User,
  FileText,
  Shield,
  Car,
  Camera,
  CreditCard,
  ThumbsUp,
  ThumbsDown,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

// ============================================================================
// DUAL STATUS SYSTEM - Badge Components
// ============================================================================

// Driver/System Submission Status Badge
// ✅ UPDATED: Now syncs with adminReviewStatus for accurate driver status display
export const SubmissionStatusBadge: React.FC<{ status: string; adminReviewStatus?: string }> = ({ status, adminReviewStatus }) => {
  const getColor = () => {
    // ✅ CRITICAL FIX: If admin has approved, show as approved (green)
    // If admin has rejected, show as rejected (red)
    // Otherwise, show based on submission status
    
    if (adminReviewStatus) {
      switch (adminReviewStatus.toLowerCase()) {
        case 'approved':
          return { bg: '#d1fae5', text: '#065f46', icon: '✅', label: 'APPROVED' };
        case 'rejected':
          return { bg: '#fee2e2', text: '#991b1b', icon: '❌', label: 'REJECTED' };
        case 'needs_revision':
          return { bg: '#fed7aa', text: '#9a3412', icon: '🔄', label: 'NEEDS REVISION' };
        case 'pending_review':
          // Fall through to check submission status
          break;
      }
    }
    
    // Default behavior based on submission status
    switch (status?.toLowerCase()) {
      case 'submitted':
      case 'verified':
      case 'completed':
        return { bg: '#d1fae5', text: '#065f46', icon: '✅', label: status?.replace('_', ' ').toUpperCase() };
      case 'approved':
        return { bg: '#d1fae5', text: '#065f46', icon: '✅', label: 'APPROVED' };
      case 'rejected':
        return { bg: '#fee2e2', text: '#991b1b', icon: '❌', label: 'REJECTED' };
      case 'in_progress':
      case 'pending':
        return { bg: '#dbeafe', text: '#1e40af', icon: '🔵', label: status?.replace('_', ' ').toUpperCase() };
      case 'not_started':
        return { bg: '#f3f4f6', text: '#6b7280', icon: '⚪', label: 'NOT STARTED' };
      default:
        return { bg: '#f3f4f6', text: '#6b7280', icon: '❓', label: status?.replace('_', ' ').toUpperCase() || 'UNKNOWN' };
    }
  };

  const colors = getColor();
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 14px',
      borderRadius: '12px',
      fontSize: '13px',
      fontWeight: '600',
      background: colors.bg,
      color: colors.text,
      border: '2px solid ' + colors.text + '20'
    }}>
      <span>{colors.icon}</span>
      {colors.label}
    </span>
  );
};

// Admin Review Status Badge
export const AdminReviewBadge: React.FC<{ status: string }> = ({ status }) => {
  const getColor = () => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return { bg: '#d1fae5', text: '#065f46', icon: '✅', label: 'Approved' };
      case 'rejected':
        return { bg: '#fee2e2', text: '#991b1b', icon: '❌', label: 'Rejected' };
      case 'pending_review':
        return { bg: '#fef3c7', text: '#92400e', icon: '🕐', label: 'Pending Review' };
      case 'needs_revision':
        return { bg: '#fed7aa', text: '#9a3412', icon: '🔄', label: 'Needs Revision' };
      default:
        return { bg: '#fef3c7', text: '#92400e', icon: '🕐', label: 'Pending Review' };
    }
  };

  const colors = getColor();
  const isPending = status?.toLowerCase() === 'pending_review';
  
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 14px',
      borderRadius: '12px',
      fontSize: '13px',
      fontWeight: '700',
      background: colors.bg,
      color: colors.text,
      border: '2px solid ' + colors.text + '40',
      animation: isPending ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none'
    }}>
      <span>{colors.icon}</span>
      {colors.label}
    </span>
  );
};

// Dual Status Display Container
export const DualStatusDisplay: React.FC<{
  submissionStatus: string;
  adminReviewStatus: string;
  stepName?: string;
}> = ({ submissionStatus, adminReviewStatus, stepName }) => {
  const needsAction = adminReviewStatus?.toLowerCase() === 'pending_review' && 
                      (submissionStatus?.toLowerCase() === 'submitted' || 
                       submissionStatus?.toLowerCase() === 'completed');
  
  return (
    <div style={{ 
      display: 'grid', 
      gridTemplateColumns: '1fr 1fr', 
      gap: '16px', 
      marginBottom: '20px',
      padding: '16px',
      background: needsAction ? '#fef3c720' : '#f8fafc',
      borderRadius: '12px',
      border: needsAction ? '2px solid #fbbf24' : '2px solid #e2e8f0'
    }}>
      <div>
        <div style={{ 
          fontSize: '11px', 
          color: '#64748b', 
          marginBottom: '8px', 
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          📊 Driver Status
        </div>
        <SubmissionStatusBadge status={submissionStatus} adminReviewStatus={adminReviewStatus} />
      </div>
      <div>
        <div style={{ 
          fontSize: '11px', 
          color: '#64748b', 
          marginBottom: '8px', 
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          👤 Admin Review
        </div>
        <AdminReviewBadge status={adminReviewStatus} />
      </div>
      {needsAction && (
        <div style={{ 
          gridColumn: '1 / -1',
          marginTop: '8px',
          padding: '12px',
          background: '#fef3c7',
          borderRadius: '8px',
          border: '1px solid #fbbf24',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '16px' }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#92400e', marginBottom: '2px' }}>
              Action Required
            </div>
            <div style={{ fontSize: '12px', color: '#78350f' }}>
              {stepName || 'This step'} has been submitted and needs admin review
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ✅ SAFE ACCESSOR - Prevents crashes when data is missing
export const safe = (obj: any, path: string, defaultValue: any = null) => {
  try {
    const value = path.split('.').reduce((acc, part) => acc?.[part], obj);
    return value !== undefined && value !== null ? value : defaultValue;
  } catch {
    return defaultValue;
  }
};

// Utility functions
export const getStepIcon = (stepNumber: number) => {
  const icons = [User, Shield, Shield, FileText, Car, FileText, Camera, CreditCard];
  return icons[stepNumber - 1] || User;
};

export const getAdjudicationBadge = (adjudication: string) => {
  const styles: Record<string, { className: string; icon: any; label: string }> = {
    pass: { className: 'badge-success', icon: CheckCircle, label: 'Passed' },
    fail: { className: 'badge-danger', icon: XCircle, label: 'Failed' },
    review: { className: 'badge-warning', icon: AlertCircle, label: 'Needs Review' },
    pending: { className: 'badge-pending', icon: Clock, label: 'Pending' }
  };

  const style = styles[adjudication] || styles.pending;
  const Icon = style.icon;

  return (
    <span className={`badge ${style.className}`}>
      <Icon size={14} /> {style.label}
    </span>
  );
};